import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import framework from 'utils/framework';
import store from 'store/Store';
import { MasterListLoaded } from 'constants/events';
import * as beautyPreferenceConstants from 'constants/beautyPreferences';
const { noPreferenceOptions, notSureOption } = beautyPreferenceConstants;
const { Application } = framework;
import deepExtend from 'utils/deepExtend';
import History from 'services/History';

const SELECTION_TYPES = {
    MULTIPLE: 'MULTIPLE',
    SINGLE: 'SINGLE'
};

const WORLD_CATEGORY_URLS = {
    skinCare: '/shop/skincare',
    makeup: '/shop/makeup-cosmetics',
    hair: '/shop/hair-products',
    fragrance: '/shop/fragrance'
};

function sortArrayObjectsByOrderId(array) {
    if (!Array.isArray(array)) {
        return [];
    }

    const orderedArray = array.map((ref, index) => ({ ref, index }));

    orderedArray.sort((a, b) => {
        const aOrder = a.ref.orderId;
        const bOrder = b.ref.orderId;

        const aHasValidOrder = Number.isFinite(aOrder);
        const bHasValidOrder = Number.isFinite(bOrder);

        if (aHasValidOrder && bHasValidOrder) {
            return aOrder - bOrder;
        }

        if (!aHasValidOrder && !bHasValidOrder) {
            return a.index - b.index; // maintain original order
        }

        return aHasValidOrder ? -1 : 1; // valid numbers come before invalid ones
    });

    return orderedArray.map(item => item.ref);
}

function getLocalStorageMasterList() {
    return Storage.local.getItem(LOCAL_STORAGE.REFINEMENTS) || {};
}

function getMasterListRefinementWorlds() {
    return getLocalStorageMasterList().refinements || [];
}

function getMasterListRefinementWorldsOrderedById() {
    const refinementWorlds = getMasterListRefinementWorlds();
    const orderedRefinementWorlds = sortArrayObjectsByOrderId(refinementWorlds);

    return orderedRefinementWorlds;
}

function getMasterListRefinementWorldByKey(key) {
    if (!key) {
        return undefined;
    }

    return getMasterListRefinementWorlds().find(world => world.key === key);
}

function getMasterListRefinementWorldByCategoryId(categoryId) {
    if (!categoryId) {
        return undefined;
    }

    return getMasterListRefinementWorlds().find(world => world.categoryId === categoryId);
}

function executeWhenMasterListLoaded(callback) {
    Application.events.onLastLoadEvent(window, [MasterListLoaded], () => {
        callback?.();
    });
}

function getFirstValidNumberFromString(str) {
    if (!str) {
        return null;
    }

    const matches = str.match(/-?\d+(\.\d+)?/g); // matches integers or decimals (positive/negative)

    if (!matches) {
        return null;
    }

    for (const match of matches) {
        const num = Number(match);

        if (!isNaN(num)) {
            return num;
        }
    }

    return null;
}

function isSomeRefinementItemVisibleInBeautyPreferences(refinement) {
    // Special handling for Color IQ and other special refinement types
    if (refinement?.selectionType === 'COLORIQ' || refinement?.key === 'colorIq') {
        return true;
    }

    return !!refinement?.items?.some(item => item.visableInBP);
}

function isSomeRefinementItemFilterableBeautyPreferences(refinement) {
    return !!refinement?.items?.some(item => item.filterable);
}

function getVisibleInBPRefinements(refinements) {
    return sortArrayObjectsByOrderId(refinements).filter(isSomeRefinementItemVisibleInBeautyPreferences);
}

function getBeautyPreferencesWorldPageInfo(worldKey, worldCategoryId) {
    let worldInfo = undefined;

    if (worldKey) {
        worldInfo = getMasterListRefinementWorldByKey(worldKey);
    } else if (worldCategoryId) {
        worldInfo = getMasterListRefinementWorldByCategoryId(worldCategoryId);
    }

    if (!worldInfo) {
        return null;
    }

    const refinements = getVisibleInBPRefinements(worldInfo.refinements);

    refinements.forEach(refinement => {
        refinement.items = sortArrayObjectsByOrderId(refinement.items);
    });

    return { ...worldInfo, refinements };
}

function isRefinementPresentOnCustomerPreference(worldRefinementKey, refinementKey) {
    const storeState = store?.getState();
    const customerPreference = storeState.user?.customerPreference || {};
    const prefs = customerPreference?.[worldRefinementKey]?.[refinementKey];

    return Array.isArray(prefs) && prefs.length > 0;
}

function mapCustomerPreferenceToValues(customerPreference = {}) {
    // Use Object.entries and reduce to build the final object
    return Object.entries(customerPreference).reduce((mappedCustomerPreference, [worldKey, worldPreferences]) => {
        const refinements = getMasterListRefinementWorldByKey(worldKey)?.refinements;

        // Check if refinements is a valid array
        if (!Array.isArray(refinements)) {
            mappedCustomerPreference[worldKey] = {};

            return mappedCustomerPreference; // Return the accumulator unchanged
        }

        const worldValues = Object.entries(worldPreferences).reduce((worldAccumulator, [refinementKey, preferenceKeys]) => {
            // Check if preferenceKeys is a valid array
            if (!Array.isArray(preferenceKeys)) {
                worldAccumulator[refinementKey] = [];

                return worldAccumulator;
            }

            const refinement = refinements.find(ref => ref.key === refinementKey);

            if (!refinement) {
                worldAccumulator[refinementKey] = [];

                return worldAccumulator;
            }

            const mappedValues = preferenceKeys.map(key => refinement.items?.find(item => item.key === key)?.value).filter(Boolean);

            // Only add to the accumulator if there are actual mapped values
            if (mappedValues.length > 0) {
                worldAccumulator[refinementKey] = mappedValues;
            }

            return worldAccumulator;
        }, {}); // Start with an empty object for the worldAccumulator

        mappedCustomerPreference[worldKey] = worldValues;

        return mappedCustomerPreference;
    }, {}); // Start with an empty object for the mappedCustomerPreference
}

function checkForColorIqProgress() {
    const storeState = store?.getState();
    const colorIQ = storeState.user?.colorIQ || storeState.user?.colorIq || [];

    return colorIQ.some(item => {
        const hasIsRecentProp = Object.prototype.hasOwnProperty.call(item, 'isRecent');

        return !hasIsRecentProp || item.isRecent === 'Y' || item.isRecent === true;
    });
}

function _isRefinementComplete(refinement, worldInfo) {
    const isColorIQ = refinement.selectionType === 'COLORIQ' || refinement.key === 'colorIq';

    return isColorIQ ? checkForColorIqProgress() : isRefinementPresentOnCustomerPreference(worldInfo?.key, refinement.key);
}

function beautyPreferencesWorldProgress(worldInfo) {
    const refinements = getVisibleInBPRefinements(worldInfo?.refinements);
    let completedRefinementsCount = 0;

    refinements?.forEach(refinement => {
        if (_isRefinementComplete(refinement, worldInfo)) {
            completedRefinementsCount++;
        }
    });

    return {
        refinementsCount: refinements.length || 0,
        completedRefinementsCount
    };
}

function isValidPreferenceEntry({ worldKey, refinementKey, refinementItemsKeys }) {
    return Boolean(worldKey && refinementKey && Array.isArray(refinementItemsKeys));
}

function addCustomerPreferenceEntries(customerPreference = {}, entries = []) {
    const newCustomerPreference = { ...customerPreference };

    entries.forEach(entry => {
        if (!this.isValidPreferenceEntry(entry)) {
            Sephora.logger.warn('Skipped invalid preference entry', entry);

            return;
        }

        const { worldKey, refinementKey, refinementItemsKeys } = entry;

        if (!newCustomerPreference[worldKey]) {
            newCustomerPreference[worldKey] = {};
        }

        newCustomerPreference[worldKey][refinementKey] = refinementItemsKeys;
    });

    return newCustomerPreference;
}

function normalizeCssHexCode(hexCode = '') {
    return `#${hexCode.replaceAll('#', '')}`;
}

// Helper function to get valid Color IQ entries
function getValidColorIQEntries(colorIQData) {
    const colorIQ = Array.isArray(colorIQData) ? colorIQData : [];
    const colorIQWithLabValue = colorIQ.filter(tone => !!tone.labValue);

    const colorIQWithCorrectLAB = colorIQWithLabValue.reduce((acc, entry) => {
        // The LAB API expects 3 values ':' separated (l:a:b), more or less is considered buggy/invalid value
        // Sometimes the labValue comes ',' separated so we replace it with ':'
        const labValueArr = entry.labValue.replaceAll(',', ':').split(':');

        // Only push valid lab entries
        if (labValueArr.length === 3) {
            acc.push(entry);
        }

        return acc;
    }, []);

    return colorIQWithCorrectLAB;
}

function mapBeautyPreferencesToRecsServiceValues(customerPreference, worldKey) {
    const prefilterExpression = [];

    if (!customerPreference || !worldKey) {
        return null;
    }

    const noOptions = [notSureOption, ...noPreferenceOptions];
    const worldRefinementsByKey = getMasterListRefinementWorldByKey(worldKey);
    const onlyVisibleBPRefinements = getVisibleInBPRefinements(worldRefinementsByKey?.refinements || []);

    onlyVisibleBPRefinements.forEach(refinement => {
        const beautyPreferenceFilter = [];
        const filterableRefinements = isSomeRefinementItemFilterableBeautyPreferences(refinement);

        if (!filterableRefinements) {
            return;
        }

        if (refinement.key === 'brands') {
            return;
        }

        const currentRefinementSavedBeautyPrefs = customerPreference[refinement?.key];

        if (!Array.isArray(currentRefinementSavedBeautyPrefs) || currentRefinementSavedBeautyPrefs.length === 0) {
            return;
        }

        refinement?.items.forEach(item => {
            if (currentRefinementSavedBeautyPrefs.includes(item.key)) {
                if (!noOptions.includes(item.key)) {
                    beautyPreferenceFilter.push({
                        name: refinement.key,
                        value: item.key
                    });
                }
            }
        });

        if (beautyPreferenceFilter?.length > 0) {
            prefilterExpression.push(...beautyPreferenceFilter);
        }
    });

    const mappedData = prefilterExpression.length > 0 ? { or: prefilterExpression } : null;

    return mappedData;
}

function buildCategoryFilterRefs(customerPreference, worldKey) {
    if (!customerPreference || !worldKey) {
        return [];
    }

    const worldPrefs = customerPreference[worldKey];

    if (!worldPrefs) {
        return [];
    }

    const noOptions = [notSureOption, ...noPreferenceOptions];
    const worldRefinementsByKey = getMasterListRefinementWorldByKey(worldKey);
    const refs = [];

    (worldRefinementsByKey?.refinements || []).forEach(refinement => {
        if (!isSomeRefinementItemFilterableBeautyPreferences(refinement)) {
            return;
        }

        if (refinement.key === 'brands') {
            return;
        }

        const savedPrefs = worldPrefs[refinement.key];

        if (!Array.isArray(savedPrefs) || savedPrefs.length === 0) {
            return;
        }

        refinement?.items?.forEach(item => {
            if (savedPrefs.includes(item.key) && item.filterable && !noOptions.includes(item.key)) {
                refs.push(`filters[${refinement.key}]=${item.key}`);
            }
        });
    });

    return refs;
}

function isAtLeastOneAnsweredForWorld(customerPreference = {}, worldKey) {
    if (!worldKey || typeof worldKey !== 'string') {
        return false;
    }

    const worldPreferences = customerPreference[worldKey];

    if (worldPreferences && typeof worldPreferences === 'object') {
        return Object.values(worldPreferences).some(preferences => Array.isArray(preferences) && preferences.length > 0);
    }

    return false;
}

// Transform old BP format to new format based on the worldKey
function getNewCustomerPreference(beautyPreferencesToSave, worldRefinements) {
    // World refinements = specific categoryId refinements coming from the master list
    if (!beautyPreferencesToSave || !worldRefinements || !Array.isArray(worldRefinements.refinements)) {
        return {};
    }

    const newCustomerPreference = {};

    for (const selectionKey of Object.keys(beautyPreferencesToSave)) {
        const selectedItems = beautyPreferencesToSave[selectionKey];

        if (!Array.isArray(selectedItems)) {
            // eslint-disable-next-line no-continue
            continue;
        }

        const refinement = worldRefinements.refinements.find(r => r.key === selectionKey);

        if (!refinement || !Array.isArray(refinement.items)) {
            // eslint-disable-next-line no-continue
            continue;
        }

        // Check if any selected item matches a refinement item
        const hasMatch = selectedItems.some(selected => refinement.items.some(item => item.key === selected.key));

        if (hasMatch) {
            // If syncedWorlds is a non-empty array, use it; otherwise use worldRefinements.key
            let targetWorlds = [];

            if (Array.isArray(refinement.syncedWorlds) && refinement.syncedWorlds.length > 0) {
                targetWorlds = refinement.syncedWorlds;
            } else {
                if (typeof worldRefinements.key === 'string') {
                    targetWorlds = [worldRefinements.key];
                }
            }

            targetWorlds.forEach(worldKey => {
                if (!newCustomerPreference[worldKey]) {
                    newCustomerPreference[worldKey] = {};
                }

                // Map to array of keys only
                newCustomerPreference[worldKey][selectionKey] = selectedItems.map(item => item.key);
            });
        }
    }

    return newCustomerPreference;
}

// Combine existing customer preference with new customer preference to maintain same format and avoid dups
function mergeCustomerPreference(existingCustomerPreferences = {}, newCustomerPreferences = {}) {
    const existingCustomerPreferencesCopy = deepExtend({}, existingCustomerPreferences);

    // Merge in new preferences
    for (const worldKey of Object.keys(newCustomerPreferences)) {
        if (!existingCustomerPreferencesCopy[worldKey]) {
            existingCustomerPreferencesCopy[worldKey] = {};
        }

        const oldWorld = existingCustomerPreferencesCopy[worldKey];
        const newWorld = newCustomerPreferences[worldKey];

        for (const refinementKey of Object.keys(newWorld)) {
            const newBeautyPrefs = Array.isArray(newWorld[refinementKey]) ? newWorld[refinementKey] : [];
            const oldBeautyPrefs = Array.isArray(oldWorld[refinementKey]) ? oldWorld[refinementKey] : [];

            if (oldBeautyPrefs.length > 0) {
                // Keep old order, add only new unique values from newBeautyPrefs at the end
                const mergedArr = oldBeautyPrefs.concat(newBeautyPrefs.filter(val => !oldBeautyPrefs.includes(val)));
                oldWorld[refinementKey] = mergedArr;
            } else {
                oldWorld[refinementKey] = [...newBeautyPrefs];
            }
        }
    }

    return existingCustomerPreferencesCopy;
}

function areAllWorldsComplete() {
    const allWorlds = getMasterListRefinementWorlds() || [];

    if (!allWorlds.length) {
        return false;
    }

    return allWorlds.every(world => {
        const { refinementsCount, completedRefinementsCount } = beautyPreferencesWorldProgress(world);

        return refinementsCount > 0 && completedRefinementsCount === refinementsCount;
    });
}

function getCustomerPreferenceProgress() {
    const allWorlds = getMasterListRefinementWorlds() || [];

    if (!allWorlds.length) {
        return 0;
    }

    const { allRefinements, completedRefinements } = allWorlds.reduce(
        (acc, world) => {
            const { refinementsCount, completedRefinementsCount } = beautyPreferencesWorldProgress(world);

            if (refinementsCount > 0) {
                acc.allRefinements += refinementsCount;
                acc.completedRefinements += completedRefinementsCount;
            }

            return acc;
        },
        { allRefinements: 0, completedRefinements: 0 }
    );

    if (allRefinements > 0) {
        return Math.round((completedRefinements / allRefinements) * 100);
    }

    return 0;
}

// Grab all allowed brand ids for each world from the master list,
// and make a flat lookup map too. You can pass a refinement key if you want to filter brands.
function buildAllowedBrandsMaps(worldKeys = [], refinementKey = 'brands') {
    const perWorld = {};
    const flatMap = {};

    (worldKeys || []).forEach(worldKey => {
        const worldInfo = getBeautyPreferencesWorldPageInfo(worldKey);
        const brandsRef = worldInfo?.refinements?.find(ref => ref.key === refinementKey);
        const items = Array.isArray(brandsRef?.items) ? brandsRef.items : [];
        const set = new Set(items.map(it => String(it.key)));

        perWorld[worldKey] = set;
        set.forEach(id => {
            flatMap[id] = true;
        });
    });

    return { perWorld, flatMap };
}

// Collect favorite brand ids from customerPreference across provided worlds
function collectFavBrandIdsFromCustomerPreference(customerPreference = {}, worldKeys = []) {
    const set = new Set();

    (worldKeys || []).forEach(worldKey => {
        const ids = customerPreference?.[worldKey]?.brands || [];
        ids.forEach(id => {
            if (id !== null && id !== undefined) {
                set.add(String(id));
            }
        });
    });

    return Array.from(set);
}

// Build up the rawData payload for updateCustomerPreference, but don't mess with other worlds
function buildFavoriteBrandsRawData({
    favBrands = [],
    toggledId = '',
    remove = false,
    customerPreference = {},
    worldKeys = [],
    allowedWorldBrandIds,
    refinementKey = 'brands'
} = {}) {
    const favBrandStrings = Array.isArray(favBrands) ? favBrands.map(String) : [];
    const perWorld = allowedWorldBrandIds || buildAllowedBrandsMaps(worldKeys, refinementKey).perWorld;
    const rawData = [];

    if (toggledId) {
        const toggledStr = String(toggledId);

        (worldKeys || []).forEach(worldKey => {
            const allowedSet = perWorld[worldKey] || new Set();

            if (!allowedSet.has(toggledStr)) {
                return;
            }

            const existing = (customerPreference?.[worldKey]?.brands || []).map(String);
            let next = existing.slice(0);

            if (remove) {
                next = next.filter(id => id !== toggledStr);
            } else if (!next.includes(toggledStr)) {
                next.push(toggledStr);
            }

            rawData.push({
                worldKey,
                refinementKey,
                refinementItemsKeys: next
            });
        });

        return rawData;
    }

    // If we didn't get a toggled brand, just figure out each world's picks from the whole favBrands list
    (worldKeys || []).forEach(worldKey => {
        const allowedSet = perWorld[worldKey] || new Set();
        const worldFavs = favBrandStrings.filter(id => allowedSet.has(id));

        if (worldFavs.length > 0) {
            rawData.push({
                worldKey,
                refinementKey,
                refinementItemsKeys: worldFavs
            });
        }
    });

    return rawData;
}

function getAllMasterListUniqueRefinements(onlyVisibleBPItems = false) {
    const masterList = getLocalStorageMasterList();
    const masterListRefinements = masterList.refinements;
    const user = store.getState().user;
    const userBeautyPreference = user.userBeautyPreference || {};

    if (!Array.isArray(masterListRefinements)) {
        return [];
    }

    const refinementMap = new Map();

    masterListRefinements.forEach(category => {
        if (!category || !Array.isArray(category.refinements)) {
            return;
        }

        category.refinements.forEach(refinement => {
            if (!refinement || !refinement.key) {
                return;
            }

            //Make Unique Refinements
            const savedRefinement = refinementMap.get(refinement.key) || { ...refinement, items: new Map(), worldKey: category.key };

            const orderedRefinementItems = sortArrayObjectsByOrderId(refinement.items || []);

            orderedRefinementItems.forEach(item => {
                const isValidItem = item && item.key;
                const isVisible = onlyVisibleBPItems ? item.visableInBP : true;

                //Make Unique Items
                if (isValidItem && isVisible) {
                    const beautyPreferenceValues = userBeautyPreference[refinement.key];
                    const isItemSelectedAsUserBeautyPreference =
                        beautyPreferenceValues && beautyPreferenceValues instanceof Array && beautyPreferenceValues?.includes(item.key);
                    savedRefinement.items.set(item.key, { ...item, isItemSelectedAsUserBeautyPreference });
                }
            });

            refinementMap.set(refinement.key, savedRefinement); // Store back after modification
        });
    });

    // Convert Refinements and Items map back to arrays
    return Array.from(refinementMap.values()).map(refinement => ({
        ...refinement,
        items: Array.from(refinement.items.values())
    }));
}

function getAllMasterListUniqueItems() {
    const refinements = getAllMasterListUniqueRefinements(false);

    const allItems = refinements.reduce((acc, refinement) => {
        const items = refinement.items || [];

        return [...acc, ...items];
    }, []);

    return allItems || [];
}

function goToCategoryPageWithBeautyPreferencesAsFilter(e, worldKey, customerPreference) {
    e.preventDefault();
    e.stopPropagation();
    const categoryUrl = WORLD_CATEGORY_URLS[worldKey];

    if (!categoryUrl) {
        return;
    }

    const refs = buildCategoryFilterRefs(customerPreference, worldKey);
    History.doSPANavigation({
        path: categoryUrl,
        queryParams: refs.length > 0 ? { ref: refs } : {}
    });
}

export default {
    SELECTION_TYPES,
    WORLD_CATEGORY_URLS,
    getLocalStorageMasterList,
    getMasterListRefinementWorlds,
    getMasterListRefinementWorldByKey,
    executeWhenMasterListLoaded,
    sortArrayObjectsByOrderId,
    getMasterListRefinementWorldsOrderedById,
    getFirstValidNumberFromString,
    isSomeRefinementItemVisibleInBeautyPreferences,
    getBeautyPreferencesWorldPageInfo,
    isRefinementPresentOnCustomerPreference,
    beautyPreferencesWorldProgress,
    addCustomerPreferenceEntries,
    isValidPreferenceEntry,
    normalizeCssHexCode,
    getValidColorIQEntries,
    getMasterListRefinementWorldByCategoryId,
    mapBeautyPreferencesToRecsServiceValues,
    isSomeRefinementItemFilterableBeautyPreferences,
    buildCategoryFilterRefs,
    isAtLeastOneAnsweredForWorld,
    mapCustomerPreferenceToValues,
    getNewCustomerPreference,
    mergeCustomerPreference,
    areAllWorldsComplete,
    buildAllowedBrandsMaps,
    collectFavBrandIdsFromCustomerPreference,
    buildFavoriteBrandsRawData,
    getCustomerPreferenceProgress,
    checkForColorIqProgress,
    getAllMasterListUniqueRefinements,
    getAllMasterListUniqueItems,
    goToCategoryPageWithBeautyPreferencesAsFilter
};
